// @ts-nocheck
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { submitOTP } from '@features/dcb-flow';
import { cleanOTPAlreadyUsed } from '@shared/hooks/useOTPPageIsUsed';
import { useApplyBESessionId } from '@shared/hooks/useApplyBESessionId';
import { useApplyGtagUser } from '@shared/hooks/useApplyGtagUser';
import { useMoroccoDVHEFlowOTP } from '@features/phone-subscription/model/hooks/useMoroccoDVHEFlowOTP';
import { useRefetchHomeData } from '@features/phone-subscription/model/hooks/useRefetchHomeData';
import { useLocationHasParticularOperator } from '@entities/operator/model/hooks/useLocationHasParticularOperator';
import { isAE, isIQ } from '@entities/country/model/utils';
import { isIraqAsiacell } from '@entities/operator';
import {
  IQ_ASIACELL_PIN_LENGTH,
  isBlockedPinValue,
  isValidPinLength,
  resolveServiceShortCode,
} from '@features/otp-verification/model/pinValidation';
import { URLS } from '@shared/config/config';
import {
  removeConversionImg,
  removeConversionScript,
  shouldRunDCEvent,
} from '@shared/utils/dc-events';

import { OTPVariant, UseOTPLogicOptions } from '@features/otp-verification/model/types';
import { useOTPSelectors } from '@features/otp-verification/model/useOTPSelectors';
import { useOTPAntifraud } from '@features/otp-verification/model/useOTPAntifraud';
import { useOTPRedirectionDetails } from '@features/otp-verification/model/useOTPRedirectionDetails';

const INPUT_ID = 'pin-input-full';
const NUMERIC_REGEXP = /^[0-9\u0660-\u0669]+$/;

const focusOtpInput = () => document.getElementById(INPUT_ID)?.focus();

const buildSubmitRoute = (_variant: OTPVariant, locale: string, sessionId: string | number) => {
  return `/thankyou/${locale}/${sessionId}`;
};

export const useOTPLogic = ({ variant = 'standard' }: UseOTPLogicOptions = { variant: 'standard' }) => {
  const history = useHistory();

  const [pin, setPin] = useState('');
  const [invalidLength, setInvalidLength] = useState(true);
  const [errors, setErrors] = useState<{ pin?: string }>({});

  const selectors = useOTPSelectors();
  const {
    alerts,
    urlConfig,
    language,
    homeData,
    subscribeData,
    operator,
    btnLoading,
    source,
    otpError,
    resendOtpData,
    reachedThankYou,
    operatorSelectedDetails,
    fallbackOperator,
    isV3Route,
  } = selectors;

  const { DLSessionId } = useApplyBESessionId();

  const isDirectRedirect = variant === 'direct-redirect';

  const antifraud = useOTPAntifraud(variant);
  const redirection = useOTPRedirectionDetails(isDirectRedirect);

  // direct-redirect manages its own antifraud id via the redirection API
  const antifraudUniqueId = isDirectRedirect
    ? redirection.antifraudUniqueId
    : antifraud.antifraudUniqueId;

  useApplyGtagUser(subscribeData?.subscriberId || null);

  useRefetchHomeData();

  const { MAHESecondClickId } = useMoroccoDVHEFlowOTP();

  const isKSAZain = useLocationHasParticularOperator(URLS.ksazain);
  const operatorIdentifier = subscribeData?.operatorName || operator?.value || fallbackOperator;
  const isIQAsiacell = isIQ(homeData?.country) && isIraqAsiacell(operatorIdentifier);
  const enforceStrictPinRules = isIQAsiacell;
  const serviceShortCode = resolveServiceShortCode(subscribeData, operatorSelectedDetails);
  const otpDigitsCount = enforceStrictPinRules
    ? IQ_ASIACELL_PIN_LENGTH
    : subscribeData?.otpDigitsCount || 4;
  const minPinLength = enforceStrictPinRules ? IQ_ASIACELL_PIN_LENGTH : otpDigitsCount;
  const maxPinLength = enforceStrictPinRules
    ? IQ_ASIACELL_PIN_LENGTH
    : subscribeData?.otpDigitsCount || 6;

  const isPinInvalid = (candidatePin: string) => {
    if (
      !isValidPinLength(candidatePin, {
        enforceStrictRules: enforceStrictPinRules,
        expectedDigits: subscribeData?.otpDigitsCount,
      })
    ) {
      return true;
    }

    return isBlockedPinValue(candidatePin, {
      enforceStrictRules: enforceStrictPinRules,
      serviceShortCode,
    });
  };

  // Submit handler – common across variants, with per-variant tweaks.
  const submitToServer = (candidatePin: string) => {
    if (!candidatePin || candidatePin.length === 0 || isPinInvalid(candidatePin)) {
      setErrors({ pin: 'Invalid' });
      return;
    }

    setErrors({});

    // SLA operators (Bahrain Zain, MAU Mobile, IQ Fastlink) must wait for the
    // external fraud detector script to load before submitting.
    if (antifraud.isSLAOperator) {
      const fraudDetectorLoaded = () => {
        const loadedEl = document.getElementById('fraudDetectorIsLoaded') as HTMLInputElement | null;
        if (loadedEl && loadedEl.value === 'yes') {
          const token = document.getElementsByName('token')?.[0] as HTMLInputElement | undefined;
          const tokenValue = token?.value || '';

          submitOTP(
              {
                sessionId: subscribeData.sessionId,
                lang: language.locale,
                otp: pin,
                source,
                antifraudUniqueId: tokenValue,
                clickId: '',
                ga4_session_id: DLSessionId,
              },
              history,
              buildSubmitRoute(variant, language.locale, subscribeData.sessionId)
            );
        } else if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(fraudDetectorLoaded);
        } else {
          setTimeout(fraudDetectorLoaded, 100);
        }
      };
      fraudDetectorLoaded();
      return;
    }

    // Standard variant attaches MAHE second click id for Morocco flow and
    // cleans up DC tracking pixels prior to verification.
    if (variant === 'standard') {
      if (shouldRunDCEvent(homeData?.source || '')) {
        removeConversionImg();
        removeConversionScript();
      }

      submitOTP(
          {
            sessionId: subscribeData.sessionId,
            lang: language.locale,
            otp: candidatePin,
            source,
            antifraudUniqueId,
            clickId: MAHESecondClickId,
          },
          history,
          buildSubmitRoute(variant, language.locale, subscribeData.sessionId)
        );
      return;
    }

    if (variant === 'v5') {
      submitOTP(
          {
            sessionId: subscribeData.sessionId,
            lang: language.locale,
            otp: candidatePin,
            source,
            antifraudUniqueId,
          },
          history,
          buildSubmitRoute(variant, language.locale, subscribeData.sessionId)
        );
      return;
    }

    // direct-redirect uses the session id loaded from /redirection/details.
    submitOTP(
        {
          sessionId: redirection.redirectionDetails?.sessionId || '',
          lang: language.locale,
          otp: candidatePin,
          source,
          antifraudUniqueId,
        },
        history,
        buildSubmitRoute(variant, language.locale, subscribeData.sessionId)
      );
  };

  const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let enteredPin = event.target.value;

    if (enteredPin !== '' && !enteredPin.match(NUMERIC_REGEXP)) return;

    if (enforceStrictPinRules && enteredPin.length > IQ_ASIACELL_PIN_LENGTH) {
      enteredPin = enteredPin.slice(0, IQ_ASIACELL_PIN_LENGTH);
    }

    const oldPinLength = pin.length;
    const newPinLength = enteredPin.length;

    setPin(enteredPin);

    const hasError = isPinInvalid(enteredPin);

    setInvalidLength(hasError);

    const skipAutoSubmitByCountry = isAE(homeData.country) || isKSAZain;
    const lengthJumpedByPaste = !hasError && newPinLength > oldPinLength + 3;

    // Auto-submit policy by variant:
    //   standard: disabled entirely (legacy keeps the branch commented-out)
    //   v5:       enabled, but suppressed for AE and KSA Zain
    //   direct-redirect: always enabled (the redirection details API has
    //                    already validated the session)
    if (variant === 'v5' && !skipAutoSubmitByCountry && lengthJumpedByPaste) {
      submitToServer(enteredPin);
    } else if (variant === 'direct-redirect' && lengthJumpedByPaste) {
      submitToServer(enteredPin);
    }
  };

  const handleSubmit = (event?: React.FormEvent | null) => {
    cleanOTPAlreadyUsed();
    event?.preventDefault?.();
    submitToServer(pin);
  };

  const handleExitPage = () => {
    window.location.href = 'https://360vuz.com';
  };

  // Focus the OTP input on first click anywhere on the page.
  useEffect(() => {
    const listener = () => {
      focusOtpInput();
      window.removeEventListener('click', listener);
    };
    window.addEventListener('click', listener);
    return () => window.removeEventListener('click', listener);
  }, []);

  useEffect(() => {
    if (reachedThankYou) history.push('/');
  }, [reachedThankYou, history]);

  // Standard and V5 auto-focus the input every time antifraud injection
  // recomputes (matches the legacy behavior of useCallback->useEffect).
  useEffect(() => {
    if (variant === 'direct-redirect') focusOtpInput();
  }, [variant]);

  return {
    // shared state
    pin,
    invalidLength,
    errors,
    antifraudUniqueId,
    inputId: INPUT_ID,
    otpDigitsCount,
    minPinLength,
    maxPinLength,

    // selectors
    ...selectors,

    // antifraud helpers
    isBHZainSLAOperator: antifraud.isBHZainSLAOperator,
    isMAUmobileOperator: antifraud.isMAUmobileOperator,
    isIQFastlink: antifraud.isIQFastlink,
    isSLAOperator: antifraud.isSLAOperator,
    resendOtpJsstring: antifraud.resendOtpJsstring,

    // direct-redirect specific
    redirectionDetails: redirection.redirectionDetails,

    // Morocco DV HE second-click id (consumed by Timer in standard)
    MAHESecondClickId,

    // handlers
    handlePinChange,
    handleSubmit,
    handleExitPage,
  };
};
